import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface NewsArticle {
  id: string;
  title: string;
  summary: string | null;
  content: string | null;
  imageUrl: string | null;
  isFeatured: boolean;
  publishedAt: string;
  createdAt: string;
}

interface UseCountryNewsOptions {
  country: string;
  limit?: number;
  featuredOnly?: boolean;
}

export const useCountryNews = (options: UseCountryNewsOptions) => {
  const { country, limit = 20, featuredOnly = false } = options;
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);

        let query = supabase
          .from('country_news')
          .select('*')
          .eq('country', country)
          .eq('is_published', true)
          .order('published_at', { ascending: false })
          .limit(limit);

        if (featuredOnly) {
          query = query.eq('is_featured', true);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        const articles: NewsArticle[] = (data || []).map(article => ({
          id: article.id,
          title: article.title,
          summary: article.summary,
          content: article.content,
          imageUrl: article.image_url,
          isFeatured: article.is_featured,
          publishedAt: article.published_at || article.created_at,
          createdAt: article.created_at,
        }));

        setArticles(articles);
      } catch (err) {
        console.error('Error fetching country news:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch news');
      } finally {
        setLoading(false);
      }
    };

    if (country) {
      fetchNews();
    }
  }, [country, limit, featuredOnly]);

  const featuredArticles = articles.filter(a => a.isFeatured);
  const regularArticles = articles.filter(a => !a.isFeatured);

  return {
    articles,
    featuredArticles,
    regularArticles,
    loading,
    error,
  };
};
